import { Component, Input, HostBinding, ChangeDetectionStrategy } from '@angular/core';

// Owns 100% of "what does an absence look like on an avatar" — the badge
// today, a ring or an icon tomorrow if that's ever wanted. To change the
// visual treatment app-wide, edit only this component's template/scss;
// nothing that uses <my-avatar> needs to know or change.
@Component({
    selector: 'my-availability-indicator',
    templateUrl: './availability-indicator.component.html',
    styleUrls: ['./availability-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityIndicatorComponent {
    // Count of weekdays (weekends excluded), from today through the end of
    // next week, this person isn't fully available. Normally capped at 10 by
    // how the window is computed (see AvailabilityService) — the two-digit
    // treatment below triggers on any value of 10 or more rather than
    // hardcoding 10, so it still holds if that window is ever widened.
    @Input() awayDays = 0;

    // Whether the person is away right now, today — gets the stronger
    // colour. Away later in the window but fine today gets the quieter one.
    @Input() awayToday = false;

    // Shows a plain colour dot instead of the day count — for a context
    // that represents more than one person at once (e.g. the "+N" overflow
    // circle in a stack), where a single number can't meaningfully stand
    // for everyone it covers.
    @Input() dot = false;

    // Smaller rendering for contexts with less room, e.g. the 20px mini
    // avatars in the extra-avatars dropdown.
    @Input() compact = false;

    // Repositions for a 24px avatar (size="small") — only meaningful for a
    // single avatar, since grouped/stacked avatars always render at 32px
    // regardless of the size input, so they stay on the default position.
    @Input() small = false;

    // Repositions for a 28px avatar (size="medium") — only meaningful for a
    // single avatar, same reasoning as `small` above.
    @Input() medium = false;

    // Repositions for a single avatar explicitly set to size="large" (32px)
    // — distinct from the default, which also covers grouped/stacked
    // avatars (also 32px, but never carry size="large" themselves).
    @Input() large = false;

    @HostBinding('class.is-compact')
    get isCompact(): boolean {
        return this.compact;
    }

    @HostBinding('class.is-small')
    get isSmall(): boolean {
        return this.small;
    }

    @HostBinding('class.is-medium')
    get isMedium(): boolean {
        return this.medium;
    }

    @HostBinding('class.is-large')
    get isLarge(): boolean {
        return this.large;
    }

    @HostBinding('class.is-today')
    get isToday(): boolean {
        return this.awayToday;
    }

    @HostBinding('class.is-dot')
    get isDot(): boolean {
        return this.dot;
    }

    // The badge is sized for a single digit; any two-digit (or longer) count
    // needs a smaller font to still fit comfortably.
    @HostBinding('class.is-two-digit')
    get isTwoDigit(): boolean {
        return !this.dot && this.awayDays >= 10;
    }
}
